import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';
import { getVisibleElements, scheduler } from '@theme/utilities';

export class SliderWithNavigation extends Component {
  requiredRefs = ['scroller', 'next', 'previous', 'navigation'];

  connectedCallback() {
    super.connectedCallback();

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.scroller = new Scroller(scroller, {
      onScroll: this.#updateButtonsState,
      onScrollEnd: this.#updateButtonsState,
    });

    this.#resizeObserver = new ResizeObserver(() => {
      scheduler.schedule(() => {
        this.#updateNavigationVisibility();
        this.#updateButtonsState();
      });
    });

    this.#resizeObserver.observe(scroller);

    // Initial check
    scheduler.schedule(() => {
      this.#updateNavigationVisibility();
      this.#updateButtonsState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.scroller) {
      this.scroller.destroy();
    }

    const { scroller } = this.refs;
    if (this.#resizeObserver && scroller instanceof HTMLElement) {
      this.#resizeObserver.disconnect();
    }
  }

  /**
   * Scrolls to the next set of visible slides.
   * @param {Event} [event]
   */
  next(event) {
    if (event) event.preventDefault();
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    const slides = this.#getSlides();
    if (!slides.length) return;

    const visibleSlides = getVisibleElements(scroller, slides, 0.5, 'x');
    const firstVisibleSlide = visibleSlides[0];
    if (!firstVisibleSlide) return;

    const nextSlide = firstVisibleSlide.nextElementSibling;

    if (
      nextSlide instanceof HTMLElement &&
      nextSlide.classList.contains('js-slide') &&
      this.scroller
    ) {
      this.scroller.to(nextSlide);
    }
  }

  /**
   * Scrolls to the previous set of visible slides.
   * @param {Event} [event]
   */
  previous(event) {
    if (event) event.preventDefault();
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    const slides = this.#getSlides();
    if (!slides.length) return;

    const visibleSlides = getVisibleElements(scroller, slides, 0.9, 'x');
    const firstVisibleSlide = visibleSlides[0];
    if (!firstVisibleSlide) return;

    const previousSlide = firstVisibleSlide.previousElementSibling;

    if (
      previousSlide instanceof HTMLElement &&
      previousSlide.classList.contains('js-slide') &&
      this.scroller
    ) {
      this.scroller.to(previousSlide);
    }
  }

  #updateNavigationVisibility() {
    const { scroller, navigation } = this.refs;
    if (
      !(scroller instanceof HTMLElement) ||
      !(navigation instanceof HTMLElement)
    )
      return;

    const slides = this.#getSlides();
    const slideWidth = slides[0] instanceof HTMLElement ? slides[0].offsetWidth : 0;
    const overflow = scroller.scrollWidth - scroller.clientWidth;
    const hasHalfSlideHidden = slideWidth > 0 && overflow > slideWidth / 2;
    navigation.classList.toggle('hidden', !hasHalfSlideHidden);
  }

  /**
   * Updates the disabled state of the next/previous buttons based on scroll position.
   */
  #updateButtonsState = () => {
    const { scroller, next, previous } = this.refs;
    if (
      !(scroller instanceof HTMLElement) ||
      !(next instanceof HTMLButtonElement) ||
      !(previous instanceof HTMLButtonElement)
    )
      return;

    const scrollLeft = scroller.scrollLeft;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

    // Use a small threshold for floating point issues
    previous.disabled = scrollLeft <= 1;
    next.disabled = scrollLeft >= maxScrollLeft - 1;
  };

  /**
   * Returns all slide elements within the scroller.
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return [];
    return Array.from(scroller.querySelectorAll('.js-slide'));
  }

  /** @type {ResizeObserver} */
  #resizeObserver;
}

if (!customElements.get('slider-with-navigation')) {
  customElements.define('slider-with-navigation', SliderWithNavigation);
}
